import type { ItemOwner, PrepDetailsItem, SetPlanItemInput } from "../types.js";

/**
 * Lógica pura da seleção de produtos da Etapa 1 (Design.md §6.1): converte as
 * linhas selecionadas no payload de PUT /inbound-plans/:id/items e valida.
 * Sem React/DOM — testável em node.
 */
export interface PlanItemSelectionRow {
  msku: string;
  asin: string;
  fnsku: string;
  productName: string;
  quantity: number;
  /** "Quem etiqueta" (§6.1). É o fallback de prepOwner quando não há constraint. */
  labelOwner: ItemOwner;
  /**
   * Data de validade (YYYY-MM-DD) informada pelo operador. Vazio = sem validade.
   * A SP-API exige `expiration` em itens datados (FEFO) no createInboundPlan.
   */
  expiration: string;
}

/**
 * Deriva o owner a partir do constraint da Amazon (listPrepDetails). Espelha
 * `ownerFromConstraint` de @ficc/sp-api: `*_ONLY` força o valor; sem constraint
 * (null/desconhecido) retorna null para o chamador aplicar seu fallback.
 */
function ownerFromConstraint(
  constraint: string | null | undefined
): ItemOwner | null {
  switch (constraint) {
    case "AMAZON_ONLY":
      return "AMAZON";
    case "NONE_ONLY":
      return "NONE";
    case "SELLER_ONLY":
      return "SELLER";
    default:
      return null;
  }
}

/** Subconjunto dos prep details usado para derivar o prepOwner de cada item. */
export type PrepOwnerDerivationSource = Pick<
  PrepDetailsItem,
  "ownerConstraints" | "prepCategory" | "prepTypes"
>;

/**
 * prepTypes que NÃO são prep físico: etiquetagem é regida por `labelOwner`, e
 * ITEM_NO_PREP declara explicitamente "sem prep". Espelha o backend
 * (create-inbound-plan-stage).
 */
const NON_PHYSICAL_PREP_TYPES: ReadonlySet<string> = new Set([
  "ITEM_LABELING",
  "ITEM_NO_PREP"
]);

/**
 * Deriva o prepOwner do item a partir do dado autoritativo da Amazon:
 * 1. constraint `*_ONLY` (específico ou allOwnersConstraint) força o valor;
 * 2. sem constraint, prepCategory NONE/UNKNOWN sem prepType físico ⇒ NONE — a
 *    Amazon rejeita o createInboundPlan com "does not require prepOwner but
 *    SELLER was assigned. Accepted values: [NONE]" nesses itens (a resposta de
 *    listPrepDetails deles vem SEM constraint, provado em prod no plano a7502b67);
 * 3. sem prep details (ou categoria de prep real sem constraint), cai no
 *    `labelOwner` escolhido pelo operador.
 */
function derivePrepOwner(
  prep: PrepOwnerDerivationSource | undefined,
  labelOwner: ItemOwner
): ItemOwner {
  if (prep === undefined) {
    return labelOwner;
  }
  const constrained = ownerFromConstraint(
    prep.ownerConstraints.prepOwnerConstraint ??
      prep.ownerConstraints.allOwnersConstraint
  );
  if (constrained !== null) {
    return constrained;
  }
  const noPhysicalPrep = prep.prepTypes.every((prepType) =>
    NON_PHYSICAL_PREP_TYPES.has(prepType)
  );
  if (
    (prep.prepCategory === "NONE" || prep.prepCategory === "UNKNOWN") &&
    noPhysicalPrep
  ) {
    return "NONE";
  }
  return labelOwner;
}

/**
 * Monta o payload de itens.
 *
 * - `prepOwner` é derivado dos prep details (ver `derivePrepOwner`): constraint
 *   `*_ONLY` força o valor; prepCategory NONE/UNKNOWN sem prep físico ⇒ `NONE`;
 *   só então cai no `labelOwner`. Isso evita a rejeição "does not require
 *   prepOwner but SELLER was assigned".
 * - `expiration` (YYYY-MM-DD) só é enviado quando preenchido: itens datados (FEFO)
 *   a exigem; itens não-datados não a aceitam.
 */
export function buildSetItemsPayload(
  rows: readonly PlanItemSelectionRow[],
  constraintByMsku: ReadonlyMap<string, PrepOwnerDerivationSource>
): SetPlanItemInput[] {
  return rows.map((row) => {
    const prepOwner = derivePrepOwner(
      constraintByMsku.get(row.msku),
      row.labelOwner
    );
    const expiration = row.expiration.trim();
    return {
      msku: row.msku,
      quantity: row.quantity,
      labelOwner: row.labelOwner,
      prepOwner,
      ...(row.fnsku.length > 0 ? { fnsku: row.fnsku } : {}),
      ...(expiration.length > 0 ? { expiration } : {})
    };
  });
}

/** Erros de seleção (mostrados antes de salvar). */
export function validateSelection(
  rows: readonly PlanItemSelectionRow[]
): string[] {
  const errors: string[] = [];
  if (rows.length === 0) {
    errors.push("Selecione ao menos um produto para enviar.");
  }
  if (rows.length > 100) {
    errors.push("No máximo 100 produtos por plano.");
  }
  const vistos = new Set<string>();
  const hoje = new Date().toISOString().slice(0, 10);
  for (const row of rows) {
    if (!Number.isInteger(row.quantity) || row.quantity < 1) {
      errors.push(`${row.msku}: informe uma quantidade inteira maior que zero.`);
    }
    // MSKU repetido: o backend gravaria os dois itens (doc-ID aleatório) e a
    // Amazon somaria/recusaria de forma difícil de diagnosticar.
    if (vistos.has(row.msku)) {
      errors.push(`${row.msku}: produto repetido na lista — mantenha uma linha por SKU.`);
    }
    vistos.add(row.msku);
    // Mesma regra do backend, aplicada antes do request: erro imediato e no
    // idioma certo, em vez de um 400 genérico depois de salvar.
    const expiration = row.expiration.trim();
    if (expiration.length > 0) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(expiration)) {
        errors.push(`${row.msku}: validade deve estar no formato AAAA-MM-DD.`);
      } else if (expiration < hoje) {
        errors.push(
          `${row.msku}: validade vencida — a Amazon recusa itens com validade no passado.`
        );
      }
    }
  }
  return errors;
}
